import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FriendlyError,
  SectionLoading,
  StarRating,
  StepHeading,
  StepShell,
} from "@codel/dova-ds";
import { TextField } from "../../components/TextField";
import { Button } from "../../onboarding/components/Button";
import { useMeContext } from "../../MeApp";
import { unexpectedDetail, unexpectedErrorDetail } from "../../expectedOutcome";
import {
  isAlreadySubmitted,
  useFeedbackState,
  useSaveFeedback,
  type FeedbackDraft,
} from "../../hooks/reflectionHooks";
import { FlowOutcomeScreen, FlowSavedScreen } from "../FlowOutcomeScreen";
import { useFlowBack } from "../useFlowBack";
import {
  FALLBACK_THERAPIST_NAME,
  FEEDBACK_CLOSED,
  FEEDBACK_COPY,
  RATING_WORDS,
  privacyLine,
  type FeedbackQuestion,
} from "./feedbackCopy";
import { classifyFeedbackError } from "./feedbackOutcome";
import { COUPLE_EVENTS } from "../../analytics/events";
import { track, useTrackOnce } from "../../analytics/track";

// FeedbackFlow — the post-session reflection (design: postsession.jsx). One
// question per screen, steps as COMPONENT STATE: star rating (required) → three
// written questions (each optional) → saved. Answers are FINAL once saved:
// re-entry after saving goes straight to the saved screen, and a racing second
// save (409 already submitted) lands there too.

// The score words are NOT declared here. A rating persists as a bare integer, so
// its word is the only record of what a 4 meant — it lives in
// `feedbackVocabulary.ts`, under the lock in `me/labelLock.ts`, and reaches this
// file re-exported through `feedbackCopy.ts`.
//
// 🔴 THE WINDOW IS A GATE, NOT A DECORATION. `GET` is documented as always-200
// (`routers/me_reflections.py:13-14`) and reports `window_open`; the `PUT`
// refuses with a 409 when that is false. This flow used to read the state and
// IGNORE the flag, so a client reaching a closed window got a fully submittable
// form whose Send could only ever fail — and failed through `FriendlyError`,
// which says "something went wrong" for what is an ordinary "not yet". Both
// routes into that state now render the same explained outcome instead.

export function FeedbackFlow() {
  const { sessionUuid = "" } = useParams();
  const navigate = useNavigate();
  const { couple } = useMeContext();
  const state = useFeedbackState(sessionUuid);
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;

  if (state.isLoading) return <SectionLoading />;
  if (state.error) {
    return (
      <StepShell>
        {/* An unexpected read failure, so `FriendlyError` is right — but it is handed the
            server's reason, not just `ApiError`'s bare "HTTP 404:" status line. */}
        <FriendlyError detail={unexpectedDetail(state.error)} />
      </StepShell>
    );
  }
  // The window never re-closes, so `window_open: false` means one thing: the session has not
  // ended. Explain it and route home — never a form that cannot be sent.
  if (state.data && !state.data.windowOpen) {
    return (
      <FlowOutcomeScreen
        outcome={FEEDBACK_CLOSED}
        therapistName={therapistName}
        onBack={() => navigate("/me/home")}
      />
    );
  }
  if (state.data?.feedback) return <FeedbackSaved />;
  return <FeedbackFlowInner sessionUuid={sessionUuid} />;
}

function FeedbackSaved() {
  const navigate = useNavigate();
  return (
    <FlowSavedScreen
      title={FEEDBACK_COPY.saved.title}
      body={FEEDBACK_COPY.saved.body}
      homeLabel={FEEDBACK_COPY.saved.homeLabel}
      onHome={() => navigate("/me/home")}
    />
  );
}

type Answers = Record<FeedbackQuestion["field"], string>;

const EMPTY_ANSWERS: Answers = { helpful: "", didntLand: "", doDifferently: "" };

/** Blank or whitespace-only answers are saved as null (the question was skipped). */
function orNull(text: string): string | null {
  return text.trim() ? text : null;
}

function FeedbackFlowInner({ sessionUuid }: { sessionUuid: string }) {
  const navigate = useNavigate();
  const { couple } = useMeContext();
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;
  const save = useSaveFeedback(sessionUuid);
  const goBack = useFlowBack();

  // 0 = star rating; 1..3 = FEEDBACK_COPY.questions[step - 1].
  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState(false);
  const [rating, setRating] = useState(0);
  const [answers, setAnswers] = useState<Answers>(EMPTY_ANSWERS);
  const questions = FEEDBACK_COPY.questions;

  // Analytics (Family G): counts and flags only — never the ratings or text.
  // The feedback read carries no standing flag, so `is_standing` is omitted.
  // `is_edit` stays on the event for dashboard continuity; answers can no longer be edited.
  useTrackOnce(COUPLE_EVENTS.reflectionStarted, { session_id: sessionUuid, is_edit: false });

  function submit() {
    const draft: FeedbackDraft = {
      rating,
      helpful: orNull(answers.helpful),
      didntLand: orNull(answers.didntLand),
      doDifferently: orNull(answers.doDifferently),
    };
    save.mutate(draft, {
      onSuccess: () => {
        track(COUPLE_EVENTS.reflectionSubmitted, {
          session_id: sessionUuid,
          is_edit: false,
          questions_answered: 1 + Object.values(answers).filter((text) => text.trim()).length,
        });
        setSaved(true);
      },
    });
  }

  // A second save means the answers are already saved: not a failure, the saved screen.
  if (saved || isAlreadySubmitted(save.error)) return <FeedbackSaved />;

  // Any other 409 on the PUT is the SAME state the window gate above explains, reached the other
  // way (a deep link raced past the gate, or a stale cached `window_open: true`). It is an answer,
  // not a crash, so it replaces the form outright; only an unexpected failure reaches
  // `FriendlyError`, and it carries the server's reason rather than a bare status line.
  const failure = classifyFeedbackError(save.error);
  if (failure?.kind === "expected") {
    return (
      <FlowOutcomeScreen
        outcome={failure.outcome}
        therapistName={therapistName}
        onBack={() => navigate("/me/home")}
      />
    );
  }
  const saveErrorDetail = unexpectedErrorDetail(failure);

  // One card shell for all four screens (the onboarding `StepShell`): progress line,
  // question, answer, then Back + the primary in the footer. Back on the first
  // screen leaves the flow the way it was entered — back to the hub card or the
  // session list that opened it, or Home when a reminder link opened it cold; on
  // the others it pops a step with answers intact.
  const totalSteps = questions.length + 1;
  const onBack = step === 0 ? goBack : () => setStep(step - 1);

  if (step > 0) {
    const question = questions[step - 1];
    const isLast = step === questions.length;
    return (
      <StepShell
        showBack
        onBack={onBack}
        backLabel={FEEDBACK_COPY.backLabel}
        currentStep={step}
        totalSteps={totalSteps}
        footer={
          isLast ? (
            <Button disabled={save.isPending} onClick={submit}>
              {FEEDBACK_COPY.saveLabel}
            </Button>
          ) : (
            <Button onClick={() => setStep(step + 1)}>{FEEDBACK_COPY.continueLabel}</Button>
          )
        }
      >
        <StepHeading title={question.title} />
        <TextField
          multiline
          id={`feedback-${question.field}`}
          label={question.title}
          labelHidden
          hint={FEEDBACK_COPY.optionalHint}
          value={answers[question.field]}
          onChange={(value) => setAnswers({ ...answers, [question.field]: value })}
          rows={4}
          placeholder={question.placeholder}
        />
        {isLast ? <FriendlyError detail={saveErrorDetail} /> : null}
      </StepShell>
    );
  }

  return (
    <StepShell
      showBack
      onBack={onBack}
      backLabel={FEEDBACK_COPY.backLabel}
      currentStep={0}
      totalSteps={totalSteps}
      footer={
        <Button disabled={!rating} onClick={() => setStep(1)}>
          {FEEDBACK_COPY.continueLabel}
        </Button>
      }
    >
      <StepHeading title={FEEDBACK_COPY.rating.title} />
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        {/* Above the stars, not in StarRating's caption: it frames the rating before it is
            given. At body size (16px, --type-body). */}
        <p className="m-0 text-center [font:var(--type-body)] text-secondary">{FEEDBACK_COPY.rating.helper}</p>
        <StarRating
          value={rating}
          onChange={setRating}
          words={RATING_WORDS}
          emptyPrompt={FEEDBACK_COPY.rating.emptyPrompt}
        />
      </div>
      <p className="m-0 text-center text-xs leading-[1.5] text-[var(--text-tertiary)]">
        {privacyLine(therapistName)}
      </p>
    </StepShell>
  );
}
